/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useConsent } from "@packages/consent";
import {
  getValidatedCookiesHlwConsent,
  URL_COOKIE_POLICY,
} from "@packages/sdk";
import { useEffect, useState } from "react";

import { numericPercentages, spacing } from "../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Accordion } from "../../_base/_core/Accordion/Accordion.emotion";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { IconButton } from "../../_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { CloseIcon } from "../../_base/icons/Icon/NavigationIcons";
import { FullscreenModal } from "../../_base/modals/FullscreenModal/FullscreenModal.emotion";
import { ModalHeader } from "../../_base/modals/ModalHeader/ModalHeader.emotion";
import { ConsentCheckbox } from "../ConsentCheckbox/ConsentCheckbox.emotion";

const accordionStyle: Interpolation = {
  width: numericPercentages[100],
};
const modalContentStyle: Interpolation = {
  paddingTop: spacing.xl,
};
const closeButtonStyle: Interpolation = {
  alignSelf: "flex-end",
};
const consentContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  [cssMediaQueryMinWidthTablet]: { flexDirection: "row" },
  marginTop: spacing.l,
};
const descriptionContainerStyle: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  marginBottom: spacing.xl,
  marginRight: spacing.xxl,
  padding: 0,
};
const formContainerStyle: Interpolation = {
  alignItems: "flex-end",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "flex-start",
  marginLeft: spacing.xxl,
  width: numericPercentages[100],
};
const linkStyle: Interpolation = {
  textDecoration: "underline",
};
const submitButtonStyle: Interpolation = {
  marginTop: spacing.l,
};
const titleStyle: Interpolation = {
  marginBottom: spacing.m,
  justifyContent: "flex-start",
};

export const CookieManager = () => {
  const t = useTranslations();

  const [consentArray, setConsentArray] = useState<number[]>([1, 2, 3]);
  const [open, setOpen] = useState<boolean>(false);

  const { saveConsent, setIsConsentModalVisible } = useConsent();

  useEffect(() => {
    window.addEventListener("hashchange", (e) => {
      const consentCookie = getValidatedCookiesHlwConsent();
      if (e.newURL.includes("cookie-manager")) {
        e.preventDefault();
        e.stopPropagation();

        // consent checkboxes should match current cookie consent
        updateConsentState();

        setOpen(true);
      } else if (open) setOpen(false);

      // if consent exists when we close CookieManager, hide ConsentModal
      if (consentCookie) setIsConsentModalVisible(false);
    });

    // handle page refresh
    if (window.location.hash.includes("cookie-manager")) setOpen(true);
    else setOpen(false);

    // consent checkboxes should match current cookie consent
    updateConsentState();
  }, []);

  const updateConsentState = () => {
    const consentCookie = getValidatedCookiesHlwConsent();

    if (consentCookie) setConsentArray(consentCookie);
    else setConsentArray([1, 2, 3]);
  };

  const close = () => {
    if (window?.location.hash.includes("cookie-manager"))
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    setOpen(false);
  };

  const updateConsentArray = (isChecked, value) => {
    const newConsentArray = [...consentArray];

    if (isChecked && !newConsentArray.includes(value))
      newConsentArray.push(value);
    else if (!isChecked && newConsentArray.includes(value)) {
      const idx = newConsentArray.indexOf(value);
      newConsentArray.splice(idx, 1);
    }

    newConsentArray.sort();
    setConsentArray(newConsentArray);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    await saveConsent({ consent: consentArray });
    close();
  };

  return (
    <FullscreenModal
      contentCSS={modalContentStyle}
      open={open}
      onOpenChange={setOpen}
      showCloseButton={false}
    >
      <IconButton
        icon={<CloseIcon />}
        onClick={() => close()}
        css={closeButtonStyle}
        variant={"white"}
      />
      <div css={consentContainerStyle}>
        <div css={descriptionContainerStyle}>
          <ModalHeader size="s" type="headline" titleCSS={titleStyle}>
            {t("cookie_manager_title")}
          </ModalHeader>
          <Text size="m" type="body">
            {t("cookie_manager_description", {
              0: t("cookie_manager_policy_link_lowercase"),
            })}
          </Text>
          <Text asChild size="m" type="body" css={linkStyle}>
            <a href={URL_COOKIE_POLICY} rel="noreferrer" target="_blank">
              {t("cookie_manager_policy_link_uppercase")}
            </a>
          </Text>
        </div>
        <div css={formContainerStyle}>
          <Accordion css={accordionStyle} type={"multiple"}>
            {/* TODO: map over checkboxes */}
            <ConsentCheckbox
              description={t("cookie_manager_necessary_cookie_para")}
              disabled={true}
              id={"necessary-approval"}
              isChecked={true}
              label={t("cookie_manager_necessary_cookie_title")}
              onCheck={updateConsentArray}
              value={0}
            />
            <ConsentCheckbox
              description={t("cookie_manager_performance_cookie_para")}
              id={"performance-approval"}
              isChecked={consentArray.includes(2)}
              label={t("cookie_manager_performance_cookie_title")}
              onCheck={updateConsentArray}
              value={2}
            />
            <ConsentCheckbox
              description={t("cookie_manager_functional_cookie_para")}
              id={"functional-approval"}
              isChecked={consentArray.includes(1)}
              label={t("cookie_manager_functional_cookie_title")}
              onCheck={updateConsentArray}
              value={1}
            />
            <ConsentCheckbox
              description={t("cookie_manager_targeting_cookie_para")}
              id={"targeting-approval"}
              isChecked={consentArray.includes(3)}
              label={t("cookie_manager_targeting_cookie_title")}
              onCheck={updateConsentArray}
              value={3}
            />
          </Accordion>
          {/* TODO: add loading state to submit button */}
          <Button
            onClick={(e) => onSubmit(e)}
            css={submitButtonStyle}
            type="submit"
            variant="color"
          >
            {t("button_save")}
          </Button>
        </div>
      </div>
    </FullscreenModal>
  );
};
